import { FeatureFlags } from "@/enums/feature-flags";
import Cookies from "js-cookie";

const FEATURE_FLAGS_COOKIE = "avezor_feature_flags";
const COOKIE_EXPIRES = 30; // 30 days

type FeatureFlagsData = Partial<Record<FeatureFlags, boolean>>;

/**
 * Get a specific feature flag value from cookies
 */
export const getFeatureFlag = (flag: FeatureFlags): boolean => {
  try {
    // Default values for flags based on environment
    const defaultValues: Record<FeatureFlags, boolean> = {
      [FeatureFlags.REALTOR_INFO_IN_ADDITIONAL_STEP]: true,
      [FeatureFlags.SMART_AUTOCOMPLETE]: true,
      [FeatureFlags.ADD_OBJECT_LOCATION]: false,
      [FeatureFlags.PROPERTIES_LIST_TABLE_VIEW]: false,
      [FeatureFlags.PROPERTIES_LIST_REALTOR_FILTERS]: false,
      [FeatureFlags.PROPERTY_HISTORY]: false,
      [FeatureFlags.PROPERTY_REALTOR_DATA]: true,
      [FeatureFlags.PROPERTY_COMMENTS]: false,
      [FeatureFlags.PROPERTY_EDIT_ACTION]: true,
      [FeatureFlags.PROPERTY_ARCHIVE_ACTION]: true,
      [FeatureFlags.PROPERTY_OTHER_ACTIONS]: false,
      [FeatureFlags.PROPERTY_STATUS]: true,
      [FeatureFlags.PROPERTY_AGENT]: false,
    };

    // Get cookie value
    const flagsData = Cookies.get(FEATURE_FLAGS_COOKIE);
    if (flagsData) {
      const flags: FeatureFlagsData = JSON.parse(flagsData);
      if (flag in flags) {
        return flags[flag] ?? defaultValues[flag];
      }
    }

    return defaultValues[flag];
  } catch (error) {
    console.error("[FeatureFlags] Error getting feature flag:", error);
    return false;
  }
};

/**
 * Set a specific feature flag value in cookies
 */
export const setFeatureFlag = (flag: FeatureFlags, enabled: boolean): void => {
  try {
    const existingData = Cookies.get(FEATURE_FLAGS_COOKIE);
    let flags: FeatureFlagsData = {};

    if (existingData) {
      flags = JSON.parse(existingData);
    }

    flags[flag] = enabled;

    Cookies.set(FEATURE_FLAGS_COOKIE, JSON.stringify(flags), {
      expires: COOKIE_EXPIRES,
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
    });
  } catch (error) {
    console.error("[FeatureFlags] Error setting feature flag:", error);
  }
};

/**
 * Get all feature flags
 */
export const getAllFeatureFlags = (): Record<FeatureFlags, boolean> => {
  const result = {} as Record<FeatureFlags, boolean>;

  Object.values(FeatureFlags).forEach((flag) => {
    result[flag] = getFeatureFlag(flag);
  });

  return result;
};

/**
 * Set multiple feature flags at once
 */
export const setMultipleFeatureFlags = (
  flags: Partial<Record<FeatureFlags, boolean>>,
): void => {
  try {
    const existingData = Cookies.get(FEATURE_FLAGS_COOKIE);
    let currentFlags: FeatureFlagsData = {};

    if (existingData) {
      currentFlags = JSON.parse(existingData);
    }

    const updatedFlags = { ...currentFlags, ...flags };

    Cookies.set(FEATURE_FLAGS_COOKIE, JSON.stringify(updatedFlags), {
      expires: COOKIE_EXPIRES,
      secure: process.env.NODE_ENV === "production",
      sameSite: "strict",
    });
  } catch (error) {
    console.error(
      "[FeatureFlags] Error setting multiple feature flags:",
      error,
    );
  }
};

/**
 * Clear all feature flags
 */
export const clearFeatureFlags = (): void => {
  Cookies.remove(FEATURE_FLAGS_COOKIE);
};

/**
 * Create console commands for all environments
 */
export const createConsoleCommands = (): void => {
  if (typeof window !== "undefined") {
    // Available flag keys for easier access
    const flagKeys = Object.values(FeatureFlags);

    (window as any).AvezorFlags = {
      // List all current flags with their states
      list: () => {
        const flags = getAllFeatureFlags();
        console.table(flags);
        return flags;
      },

      // Show available flag keys
      keys: () => {
        console.log("Available feature flags:");
        flagKeys.forEach((key) => console.log(`  - ${key}`));
        return flagKeys;
      },

      // Enable a specific flag
      enable: (flag: FeatureFlags) => {
        if (!flagKeys.includes(flag)) {
          console.error(
            `❌ Unknown flag: ${flag}. Use AvezorFlags.keys() to see available flags.`,
          );
          return;
        }
        setFeatureFlag(flag, true);
        console.log(`✅ Enabled: ${flag}`);
        // Trigger page reload to apply changes
        setTimeout(() => window.location.reload(), 100);
      },

      // Disable a specific flag
      disable: (flag: FeatureFlags) => {
        if (!flagKeys.includes(flag)) {
          console.error(
            `❌ Unknown flag: ${flag}. Use AvezorFlags.keys() to see available flags.`,
          );
          return;
        }
        setFeatureFlag(flag, false);
        console.log(`❌ Disabled: ${flag}`);
        // Trigger page reload to apply changes
        setTimeout(() => window.location.reload(), 100);
      },

      // Toggle a flag
      toggle: (flag: FeatureFlags) => {
        if (!flagKeys.includes(flag)) {
          console.error(
            `❌ Unknown flag: ${flag}. Use AvezorFlags.keys() to see available flags.`,
          );
          return;
        }
        const currentValue = getFeatureFlag(flag);
        setFeatureFlag(flag, !currentValue);
        console.log(`🔄 Toggled ${flag}: ${currentValue} → ${!currentValue}`);
        // Trigger page reload to apply changes
        setTimeout(() => window.location.reload(), 100);
      },

      // Enable multiple flags at once
      enableAll: (flags: FeatureFlags[]) => {
        const validFlags = flags.filter((flag) => flagKeys.includes(flag));
        const invalidFlags = flags.filter((flag) => !flagKeys.includes(flag));

        if (invalidFlags.length > 0) {
          console.error(`❌ Unknown flags: ${invalidFlags.join(", ")}`);
        }

        if (validFlags.length > 0) {
          const flagsToSet = {} as Partial<Record<FeatureFlags, boolean>>;
          validFlags.forEach((flag) => (flagsToSet[flag] = true));
          setMultipleFeatureFlags(flagsToSet);
          console.log(
            `✅ Enabled ${validFlags.length} flags: ${validFlags.join(", ")}`,
          );
          setTimeout(() => window.location.reload(), 100);
        }
      },

      // Clear all flags
      clear: () => {
        clearFeatureFlags();
        console.log("🧹 All feature flags cleared");
        setTimeout(() => window.location.reload(), 100);
      },

      // Get current environment info
      env: () => {
        const info = {
          NODE_ENV: process.env.NODE_ENV,
          NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
          environment:
            process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV || "unknown",
        };
        console.table(info);
        return info;
      },

      // Show help
      help: () => {
        console.log("🚩 Avezor Feature Flags Console Commands:");
        console.log(
          "  AvezorFlags.list() - List all flags and their current state",
        );
        console.log("  AvezorFlags.keys() - Show available flag keys");
        console.log(
          "  AvezorFlags.enable('flagName') - Enable a specific flag",
        );
        console.log(
          "  AvezorFlags.disable('flagName') - Disable a specific flag",
        );
        console.log(
          "  AvezorFlags.toggle('flagName') - Toggle a specific flag",
        );
        console.log(
          "  AvezorFlags.enableAll(['flag1', 'flag2']) - Enable multiple flags",
        );
        console.log("  AvezorFlags.clear() - Clear all flags");
        console.log("  AvezorFlags.env() - Show environment info");
        console.log("  AvezorFlags.help() - Show this help");
        console.log("");
        console.log("Available flags:");
        flagKeys.forEach((key) => console.log(`  - ${key}`));
        console.log("");
        console.log("Examples:");
        console.log("  AvezorFlags.enable('realtorInfoInAdditionalStep')");
        console.log("  AvezorFlags.toggle('smartAutocomplete')");
        console.log(
          "  AvezorFlags.enableAll(['realtorDashboard', 'propertyTableView'])",
        );
      },
    };

    // Show help on first load
    const env =
      process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV || "unknown";
    console.log(`🚩 Avezor Feature Flags available in console (${env})`);
    console.log("Type AvezorFlags.help() for available commands");
  }
};
